// Supabase client configuration
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug logging to help identify configuration issues (dev only)
if (import.meta.env.DEV) {
  console.log('🔍 Supabase Configuration Debug:');
  console.log('URL set:', !!supabaseUrl);
  console.log('Anon Key set:', !!supabaseAnonKey);
  console.log('URL includes placeholder:', supabaseUrl?.includes('your_'));
  console.log('Key includes placeholder:', supabaseAnonKey?.includes('your_'));
}

// Declare the supabase client variable at module level
export let supabase: any;
export let isConfigured = false;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey || 
    supabaseUrl === 'your_supabase_project_url' || 
    supabaseAnonKey === 'your_supabase_anon_key' ||
    supabaseUrl.includes('your_supabase_') || 
    supabaseAnonKey.includes('your_supabase_')) {
  console.warn('⚠️ Supabase not configured - using mock client');
  console.warn('📝 To enable database features:');
  console.warn('1. Copy .env.example to .env');
  console.warn('2. Replace placeholder values with your actual Supabase credentials');
  console.warn('3. Get your credentials from: https://supabase.com/dashboard/project/[your-project]/settings/api');
  
  // Create a minimal mock client that keeps the UI running when not configured
  // NOTE: This does NOT simulate a logged-in user.
  const mockClient = {
    auth: {
      signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not configured' } }),
      signUp: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not configured' } }),
      signOut: () => Promise.resolve({ error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: (callback: any) => {
        // Immediately report signed out so the app shows Login
        setTimeout(() => callback('SIGNED_OUT', null), 0);
        return { data: { subscription: { unsubscribe: () => {} } } };
      }
    },
    from: () => ({
      select: () => Promise.resolve({ data: [], error: null }),
      insert: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      update: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      delete: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      eq: () => ({ select: () => Promise.resolve({ data: [], error: null }) }),
      single: () => Promise.resolve({ data: null, error: null })
    })
  };
  
  supabase = mockClient;
  isConfigured = false;
} else {
  console.log('✅ Supabase configured successfully');
  supabase = createClient(supabaseUrl, supabaseAnonKey);
  isConfigured = true;
}

// Export the supabase client at module level
// Also expose globally for convenient debugging in the browser console
try {
  if (typeof window !== 'undefined') {
    (window as any).supabase = supabase;
  }
} catch {}
